import { Alert, Badge, Button, Grid, Group, Paper, Stack, Text, Title } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconArmchair, IconArrowLeft, IconLock, IconUsers } from '@tabler/icons-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { AssetImage } from '@/components/ui/AssetImage';
import { PageHeader } from '@/components/ui/PageHeader';
import { ErrorState, LoadingState } from '@/components/ui/StateViews';
import { lockedCopy } from '@/features/content/accessCopy';
import { toUserMessage } from '@/lib/api';
import { createGameSession, fetchGame, GAME_SESSIONS_KEY, gameKey, gameSessionKey } from './api';
import { DemoGameDetail } from './demo/DemoGameDetail';
import { GameRulesButton } from './GameRulesButton';
import { LockedOffer } from '@/features/access/PlanOffer';

export function GameDetailPage() {
  const { gameId = '' } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const query = useQuery({ queryKey: gameKey(gameId), queryFn: () => fetchGame(gameId), enabled: Boolean(gameId) });
  const create = useMutation({
    mutationFn: (playSelf: boolean) => createGameSession(gameId, { playSelf }),
    onSuccess: (session) => {
      queryClient.setQueryData(gameSessionKey(session.id), session);
      void queryClient.invalidateQueries({ queryKey: GAME_SESSIONS_KEY });
      void navigate(`/games/${session.game.slug}/session/${session.id}`);
    },
    onError: (error) => notifications.show({ color: 'red', message: toUserMessage(error) }),
  });

  if (query.isLoading) return <LoadingState label="Загружаем игру..." />;
  if (query.isError || !query.data) return <ErrorState onRetry={() => void query.refetch()} />;
  const game = query.data;
  /* Демо — своя страница: выбор сценария и свои партии. Полная игра — как была. */
  if (game.variant === 'demo') return <DemoGameDetail game={game} />;
  const locked = lockedCopy(game.access);

  return (
    <>
      <PageHeader
        eyebrow="Игры"
        title={game.title}
        description={game.description}
        actions={<Button component={Link} to="/games" variant="subtle" leftSection={<IconArrowLeft size={16} />}>К списку</Button>}
      />
      <Grid gutter="xl">
        <Grid.Col span={{ base: 12, md: 6 }}>
          <Paper withBorder style={{ overflow: 'hidden' }}>
            <AssetImage src={game.coverImage} alt={game.title} ratio="1 / 1" fallbackLabel="Игровое поле" />
          </Paper>
        </Grid.Col>
        <Grid.Col span={{ base: 12, md: 6 }}>
          <Stack gap="lg">
            {game.board ? (
              <Group gap="xs">
                <Badge color="gold">{game.board.cells.length} клетки</Badge>
                <Badge variant="light">{game.board.sectors.length} секторов</Badge>
                <Badge variant="default">Один круг</Badge>
              </Group>
            ) : null}
            {/* Путеводитель ведущего — только при открытой игре: в нём материалы полной версии. */}
            {game.access.allowed ? (
              <Group>
                <GameRulesButton gameSlug={game.slug} label="Путеводитель для ведущих" />
              </Group>
            ) : null}
            {locked ? (
              <Alert color="gold" icon={<IconLock size={18} />} title={locked.reason}>
                <Stack gap="sm">
                  {locked.hint ? <Text size="sm">{locked.hint}</Text> : null}
                  {!game.access.allowed ? <LockedOffer access={game.access} gameId={game.id} /> : null}
                  {/* Закрытая полная игра — не тупик: демо даёт попробовать её часть. */}
                  {game.relatedDemo?.accessible ? (
                    <Group>
                      <Button
                        size="xs"
                        component={Link}
                        to={`/games/${game.relatedDemo.slug}`}
                        data-testid="try-demo"
                      >
                        Попробовать демоверсию
                      </Button>
                    </Group>
                  ) : null}
                </Stack>
              </Alert>
            ) : (
              <>
                <div>
                  <Title order={3}>Сессия с ведущим</Title>
                  <Text c="var(--text-muted)" mt="xs">
                    Создайте комнату, пригласите участников по ссылке и ведите партию с этого экрана.
                  </Text>
                </div>
                <Button
                  size="lg"
                  leftSection={<IconUsers size={19} />}
                  loading={create.isPending}
                  onClick={() => create.mutate(false)}
                >
                  Создать игровую сессию
                </Button>
                {/*
                  Пройти игру можно и одному — Дарья проходит её сама, чтобы
                  разобраться и вести потом. Это не другой режим: ведущий сразу
                  занимает место за собственным столом, а позвать людей по
                  ссылке можно и после.
                */}
                <Button
                  size="lg"
                  variant="light"
                  leftSection={<IconArmchair size={19} />}
                  loading={create.isPending}
                  onClick={() => create.mutate(true)}
                >
                  Играть самостоятельно
                </Button>
              </>
            )}
          </Stack>
        </Grid.Col>
      </Grid>
    </>
  );
}
